import { secondsToMilliseconds } from 'date-fns';
import { TFunction } from 'i18next';
import { useCallback } from 'react';

import { useTranslate } from '@growtherapy/i18n';

import { RejoinModalViewModel } from '../../../session/rejoin/types';
import {
  REJOIN_ERROR_TOAST_TAGS,
  RejoinError,
  useBaseRejoinModalViewModel,
} from '../../../session/rejoin/useBaseRejoinModalViewModel';
import { toast } from '../../../toasts';
import { wait } from '../../../utils';

function errorToToastMessage(error: RejoinError, t: TFunction): string {
  const errorKind = 'kind' in error ? error.kind : 'unexpected';
  switch (errorKind) {
    case 'empty-room':
      return t('telehealth-core:rejoin.yourProviderMayHaveLeftRejoiningWaitingRoom');
    default:
      return t('telehealth-core:rejoin.unexpectedErrorRejoiningWaitingRoom');
  }
}

function showErrorToast(error: RejoinError, t: TFunction) {
  toast.error(errorToToastMessage(error, t), {
    purpose: 'system',
    timeout: Infinity,
    tags: REJOIN_ERROR_TOAST_TAGS,
  });
}

function reloadPage() {
  location.reload();
}

export function useVisitorRejoinModalViewModel(): RejoinModalViewModel {
  const t = useTranslate();
  const { isLoading, isOpen, rejoin, closeModal } = useBaseRejoinModalViewModel();

  const handleRejoin = useCallback(() => {
    rejoin({
      t,
      onTerminalFailure: async (error) => {
        showErrorToast(error, t);
        closeModal();
        // Allow some time to read toast before reloading the page
        return wait(secondsToMilliseconds(3)).finally(reloadPage);
      },
    });
  }, [t, closeModal, rejoin]);

  return {
    isOpen,
    isLoading,
    onJoin: handleRejoin,
  };
}
